import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginForm from './components/Login/LoginForm';
import ChatInterface from './components/Chat/ChatInterface';
import AuthService from './services/AuthService';
import AvatarPage from './pages/AvatarPage';
import ChatPage from './pages/ChatPage';
import InteractiveAvatarPage from './pages/InteractiveAvatarPage';

const NMPLogo = require('./Images/NMP.png');

const App: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    setIsAuthenticated(AuthService.isAuthenticated());
    setLoading(false);
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    AuthService.logout();
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <LoadingContainer>
        <LoadingText>Cargando SkyBot...</LoadingText>
      </LoadingContainer>
    );
  }

  return (
    <Router>
      <AppContainer>
        <Routes>
          <Route 
            path="/login" 
            element={
              !isAuthenticated ? (
                <LoginPage>
                  <LoginForm 
                    onLoginSuccess={handleLoginSuccess}
                    onSwitchToRegister={() => {}} // TODO: Implement register
                  />
                </LoginPage>
              ) : (
                <Navigate to="/interactive" replace />
              )
            } 
          />
          
          {/* Nueva ruta del Avatar Interactivo */}
          <Route 
            path="/interactive" 
            element={
              isAuthenticated ? (
                <InteractiveAvatarPage />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Ruta del Avatar (solo prueba) */}
          <Route 
            path="/avatar" 
            element={
              isAuthenticated ? (
                <AvatarPage />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Ruta del Chat tradicional */}
          <Route 
            path="/chat" 
            element={
              isAuthenticated ? (
                <ChatPageContainer>
                  <Header>
                    <HeaderLeft>
                      <LogoContainer>
                        <LogoIcon>
                          <img src={NMPLogo} alt="Nacional Monte de Piedad" style={{ width: '75px', height: '75px', objectFit: 'contain' }} />
                        </LogoIcon>
                        <div>
                          <h1>SkyBot IA</h1>
                            <StatusDot />
                        </div>
                      </LogoContainer>
                    </HeaderLeft>
                    <LogoutButton onClick={handleLogout}>
                      Cerrar Sesión
                    </LogoutButton>
                  </Header>
                  <ChatInterface />
                </ChatPageContainer>
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          <Route path="/" element={<Navigate to={isAuthenticated ? "/interactive" : "/login"} replace />} />
        </Routes>
      </AppContainer>
    </Router>
  );
};

// Keyframes
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const logoFloat = keyframes`
  0%, 100% { transform: translateY(0) rotateY(0deg); }
  50% { transform: translateY(-5px) rotateY(5deg); }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`;

// Styled Components
const AppContainer = styled.div`
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #98012E 0%, #2c0a0f 100%);
  overflow: hidden;
`;

const LoginPage = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, #98012E 0%, #2c0a0f 100%);
`;

const ChatPageContainer = styled.div`
  height: 100vh;
  width: 100vw;
  color: white;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #98012E, #CA7C8E, #98012E);
  background-size: 200% 200%;
  animation: ${gradientShift} 4s ease infinite;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 2;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 30px;
`;

const LogoIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(145deg, #ffffff, #f5f5f5);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #98012E;
  animation: ${logoFloat} 3s ease-in-out infinite;
  box-shadow: 
    0 8px 20px rgba(0, 0, 0, 0.15),
    inset 0 2px 4px rgba(152, 1, 46, 0.1),
    0 0 0 2px rgba(152, 1, 46, 0.1);
  border: 1px solid rgba(152, 1, 46, 0.2);
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(76, 175, 80, 0.2);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  margin-top: 5px;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  background: #4CAF50;
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
`;

const StatusText = styled.span`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const LoadingContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #98012E 0%, #2c0a0f 100%);
`;

const LoadingText = styled.div`
  color: white;
  font-size: 1.5rem;
  font-weight: 300;
`;

export default App;
